import { useQueryClient } from '@tanstack/react-query';

import { toast } from 'sonner';

import {
  useComplianceAutoOpenStore,
  useComplianceRequestsStore,
  useSendDocsToReview,
  useUploadHook,
} from '../model';
import { ComplianceFormSubmit } from './ComplianceModal';
import { UploadBar } from './UploadBar';

export const ComplianceBar: React.FC<{
  paymentId?: string;
  variant?: 'dashboard' | 'transaction';
  autoOpen?: boolean;
  onAdd?: () => void;
}> = ({ paymentId, variant = 'dashboard', autoOpen = false, onAdd }) => {
  const queryClient = useQueryClient();
  const { needActionPayments, setShouldAutoOpenComplianceModal } =
    useComplianceRequestsStore();
  const { uploadDoc, isLoading: isUploadLoading } = useUploadHook();
  const { sendDocs, isPending: isSendDocsPending } = useSendDocsToReview();
  const isInPayment = useComplianceAutoOpenStore((state) => state.isInPayment)

  if (!needActionPayments?.length) return null;
  const payment =
    needActionPayments.find((i) => i.id === paymentId) ?? needActionPayments[0];

  const onFormSubmit: ComplianceFormSubmit = async ({
    data,
    files,
    requestId,
  }) => {
    if (!requestId) return;
    try {
      const responseAttachments = await Promise.all(
        files.map(({ file, customFileName }) =>
          uploadDoc(file, customFileName),
        ),
      );
      await sendDocs({
        id: requestId,
        responseText: data.comment,
        responseAttachments,
      });
      await queryClient.invalidateQueries({
        queryKey: ['need-action-payments'],
      });
      await queryClient.invalidateQueries({ queryKey: ['txs-list'] });
      onAdd?.()
      toast.success('Purpose of payment added');
    } catch (e) {
      toast.error('Upload error');
      throw new Error('upload err');
    }
  };

  const isSubmitting = isUploadLoading || isSendDocsPending;
  return (
    <UploadBar
      payment={payment}
      title="We need some information"
      subtitle="Clarify payment purpose"
      modalSubtitle="Kindly clarify the detailed purpose of payment"
      buttonText="Add"
      onFormSubmit={onFormSubmit}
      isSubmitting={isSubmitting}
      autoOpen={autoOpen && !isInPayment && payment.id === needActionPayments[0]?.id}
      onAutoOpenDismiss={() => {
        setShouldAutoOpenComplianceModal(false);
      }}
      className={variant === 'transaction' ? 'mb-1 bg-base-300' : undefined}
    />
  );
};
